// tslint:disable:max-classes-per-file max-line-length

/**
 * Possible tooth states
 */
export enum ToothState {
    /**
     * Own healthy tooth
     */
    Healthy,

    /**
     * The tooth is missing (extracted)
     */
    Missing,

    /**
     * The tooth has been replaced with an implant
     */
    Implant,

    /**
     * The tooth has moved left
     */
    MovedLeft,

    /**
     * The tooth has moved right
     */
     MovedRight,

    /**
     * This is not a state, but serves to automatically provide how many states there are
     */
    StateCount,
}


export class Tooth {
    /**
     * Tooth number, in FDI notation
     *
     * https://en.wikipedia.org/wiki/FDI_World_Dental_Federation_notation
     */
    get fdi(): number {
        return this.quadrantCode * 10 + this.toothCode;
    }

    /**
     * State of the tooth
     */
    public state = ToothState.Healthy;

    /**
     * Creates an instance of Tooth class
     *
     * @param quadrant The quadrant that the tooth is in (1-4)
     * @param number The index of the tooth in the quadrant (1-8)
     */
    constructor(
        public readonly quadrantCode: number,
        public readonly toothCode: number,
    ) {
        if (quadrantCode < 1 || quadrantCode > 4) {
            throw new Error(`Invalid quadrant code: ${quadrantCode}`);
        }

        if (toothCode < 1 || toothCode > 8) {
            throw new Error(`Invalid tooth code: ${toothCode}`);
        }
    }
}

/**
 * Represents an interdental space between two teeth
 */
export interface InterdentalSpace {
    /**
     * The tooth on one side of the space
     */
    readonly left: Tooth;

    /**
     * The tooth on the other side
     */
    readonly right: Tooth;
}

/**
 * Represents a dental chart with teeth and interdental spaces
 *
 * Note: This class uses concepts of "left" and "right" tooth. In this case, for teeth in the same quadrant, left.toothCode < right.toothCode
 */
export class Chart {
    public static isValidInterdentalSpace(space: InterdentalSpace) {
        return space.left
            && space.right
            && (space.left.state !== ToothState.Missing || space.right.state !== ToothState.Missing)
            && (space.left.state !== ToothState.Missing || space.right.state !== ToothState.MovedLeft)
            && (space.right.state !== ToothState.Missing || space.left.state !== ToothState.MovedRight)
            && !(
                // Additional requirement: when tooth no. 7 is missing, there is no interdental space
                // between 7 & 8
                   (space.right.toothCode === 8 && space.left.toothCode === 7 && space.left.state === ToothState.Missing)
                || (space.left.toothCode === 8 && space.right.toothCode === 7 && space.right.state === ToothState.Missing)
            );
    }

    public readonly teeth: readonly Tooth[];
    private readonly interdental: readonly InterdentalSpace[];

    /**
     * Gets interdental spaces. The valid (and available) spaces depend on the state of the teeth.
     */
    get interdentalSpaces(): readonly InterdentalSpace[] {
        return this.interdental.filter((s) => Chart.isValidInterdentalSpace(s));
    }

    constructor() {
        const teeth = [];
        for (let q = 1; q <= 4; ++q) {
            for (let n = 1; n <= 8; ++n) {
                teeth.push(new Tooth(q, n));
            }
        }

        this.teeth = teeth;

        const interdental = [];
        for (let q = 1; q <= 4; ++q) {
            for (let n = 8; n >= 2; --n) {
                interdental.push({
                    left: this.getTooth(q, n - 1),
                    right: this.getTooth(q, n),
                });
            }
        }
        this.interdental = interdental;
    }

    public getToothByFdi(fdi: number): Tooth {
        const result = this.teeth.find((t) => t.fdi === fdi);
        if (!result) {
            throw new Error(`Can't find tooth ${fdi} `);
        }
        return result;
    }

    public getTooth(quadrantCode: number, toothCode: number): Tooth {
        const result = this.tryGetTooth(quadrantCode, toothCode);
        if (!result) {
            throw new Error(`Can't find tooth ${toothCode} in quadrant ${quadrantCode}`);
        }
        return result;
    }

    public getAllowedStates(tooth: Tooth) {
        const result = [
            ToothState.Healthy,
            ToothState.Missing,
            ToothState.Implant,
        ];

        const left = this.tryGetTooth(tooth.quadrantCode, tooth.toothCode - 1);
        if (left?.state === ToothState.Missing) {
            result.push(ToothState.MovedLeft);
        }

        const right = this.tryGetTooth(tooth.quadrantCode, tooth.toothCode + 1);
        if (right?.state === ToothState.Missing) {
            result.push(ToothState.MovedRight);
        }

        return result;
    }

    public getMoveTarget(tooth: Tooth) {
        if (tooth.state !== ToothState.MovedLeft && tooth.state !== ToothState.MovedRight) {
            return undefined;
        }

        let neighborCode: number;
        if (tooth.state === ToothState.MovedLeft) {
            neighborCode = tooth.toothCode + ( tooth.quadrantCode === 2 || tooth.quadrantCode === 4 ? -1 : +1 );
        } else {
            neighborCode = tooth.toothCode + ( tooth.quadrantCode === 2 || tooth.quadrantCode === 4 ? +1 : -1 );
        }

        return this.getTooth(tooth.quadrantCode, neighborCode);
    }

    public setState(tooth: Tooth, state: ToothState) {
        if (tooth.state === ToothState.Missing && state !== ToothState.Missing) {
            // When the tooth is no longer missing, adjacent teeth may need to change state as well
            const rightMovedState = tooth.quadrantCode === 2 || tooth.quadrantCode === 4 ? ToothState.MovedLeft : ToothState.MovedRight;
            const leftMovedState = tooth.quadrantCode === 2 || tooth.quadrantCode === 4 ? ToothState.MovedRight : ToothState.MovedLeft;

            const left = this.tryGetTooth(tooth.quadrantCode, tooth.toothCode - 1);
            if (left?.state === leftMovedState) {
                left.state = ToothState.Healthy;
            }

            const right = this.tryGetTooth(tooth.quadrantCode, tooth.toothCode + 1);
            if (right?.state === rightMovedState) {
                right.state = ToothState.Healthy;
            }
        }

        tooth.state = state;
    }

    private tryGetTooth(quadrantCode: number, toothCode: number) {
        return this.teeth.find((t) => t.quadrantCode === quadrantCode && t.toothCode === toothCode);
    }
}

export abstract class BleedingIndex {
    public readonly value: number;
    public readonly bleedingCount: number;

    constructor(chart: Chart, bleeding: readonly InterdentalSpace[]) {
        // Ensure we only include valid spaces in the count
        // We allow invalid spaces in the array, as it is easier for the client
        this.bleedingCount = bleeding.filter((b) => Chart.isValidInterdentalSpace(b)).length;
        const totalSpaces = chart.interdentalSpaces.length;

        this.value = this.bleedingCount / totalSpaces * 100;
    }
}

export class SulcusBleedingIndex extends BleedingIndex {
    constructor(chart: Chart, bleeding: readonly InterdentalSpace[]) {
        super(chart, bleeding);
    }
}

export class ApproximalPlaqueIndex extends BleedingIndex {
    constructor(chart: Chart, bleeding: readonly InterdentalSpace[]) {
        super(chart, bleeding);
    }
}
